import {
  Box,
  Button,
  Card,
  Container,
  Group,
  Image,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconArrowRight,
  IconHeart,
  IconStar,
  IconTrophy,
  IconUsers,
} from '@tabler/icons-react';
import { Link } from 'react-router';

const values = [
  {
    icon: IconHeart,
    title: 'Compassion',
    description: 'We treat every resident with kindness, empathy, and genuine care.',
  },
  {
    icon: IconStar,
    title: 'Excellence',
    description: 'We strive for the highest standards in everything we do.',
  },
  {
    icon: IconUsers,
    title: 'Community',
    description: 'We foster meaningful connections and a sense of belonging.',
  },
  {
    icon: IconTrophy,
    title: 'Dignity',
    description: 'We honor and respect the individuality of each resident.',
  },
];

export function AboutPage() {
  return (
    <>
      {/* Hero Section */}
      <Box py={80} bg="gray.0">
        <Container size="xl">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={50}>
            <Stack justify="center" gap="lg">
              <Text c="harmony.6" fw={600} tt="uppercase" size="sm">
                About Us
              </Text>
              <Title>
                A Place Where Care Meets Compassion
              </Title>
              <Text c="dimmed" size="lg">
                Hillside Harmony is more than just an assisted
                living community. Founded on the belief that every person
                deserves to age with dignity, purpose, and joy.
              </Text>
              <Text c="dimmed">
                Our story began in 2003 when our founders recognized the need for a different
                approach to senior care. They envisioned a place where clinical excellence
                meets genuine warmth, where residents thrive in a supportive community
                that feels like family.
              </Text>
            </Stack>
            <Box>
              <Image
                src="/hillside-harmony-frontdesk.jpg"
                alt="Hillside Harmony front desk and welcoming lobby area"
                radius="lg"
                h={400}
                fit="cover"
              />
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Values Section */}
      <Box py={80}>
        <Container size="xl">
          <Stack align="center" gap="sm" mb={50}>
            <Text c="harmony.6" fw={600} tt="uppercase" size="sm">
              Our Values
            </Text>
            <Title order={2} ta="center">
              What Guides Us Every Day
            </Title>
          </Stack>

          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="xl">
            {values.map((value) => (
              <Card key={value.title} padding="xl" ta="center">
                <ThemeIcon
                  size={60}
                  radius="xl"
                  variant="light"
                  color="harmony"
                  mx="auto"
                  mb="md"
                >
                  <value.icon size={30} stroke={1.5} />
                </ThemeIcon>
                <Text fw={600} size="lg" mb="xs">
                  {value.title}
                </Text>
                <Text c="dimmed" size="sm">
                  {value.description}
                </Text>
              </Card>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={80}>
        <Container size="md">
          <Card padding="xl" radius="lg" bg="harmony.0">
            <Stack align="center" gap="lg">
              <Title order={2} ta="center">
                Come See What Makes Us Different
              </Title>
              <Text c="dimmed" ta="center" maw={500}>
                We invite you to visit Hillside Harmony and experience our warm,
                welcoming community firsthand. Schedule a tour today.
              </Text>
              <Group gap="md">
                <Button
                  component={Link}
                  to="/contact"
                  size="lg"
                  rightSection={<IconArrowRight size={18} />}
                >
                  Schedule a Tour
                </Button>
                <Button
                  component="a"
                  href="tel:+15551234567"
                  size="lg"
                  variant="outline"
                >
                  Call Us
                </Button>
              </Group>
            </Stack>
          </Card>
        </Container>
      </Box>
    </>
  );
}
